import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import merchantsApi, { useCreateMerchantsBankAccountMutation } from 'services/merchants';
import { BridgeFiatRail, ExternalAccountType, FiatPayoutSettingsPayload, MerchantPayoutFlow } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { FormField } from 'components/form/formField/formField';
import { Input } from 'components/inputs';
import { Select, SelectItem } from 'components/inputs/select/select';
import { Buttons } from 'components/modal/styles';

import { ReduxToolkitError } from 'types/http';

import Logger from 'utils/logger';

import { usePayout } from '../../../payoutModal/context';
import { usePayoutSetupStep } from '../../../payoutModal/context/modal';
import { ErrorText } from '../../../payoutModal/fiat/styles';
import { Delimiter } from '../../../payoutModal/styles';
import { PayoutType } from '../../../payoutModal/types';
import { LABELS } from '../../keys';
import { Label, Section } from '../form.styles';
import {
  PixDestinationType,
  PixFormValues,
  getPixDestinationOptions,
  getPixInitialValues,
  getPixValidationSchema,
  normalizeDocumentDigits,
} from './pix.keys';

const PixBankAccountForm = () => {
  const { t } = useTranslation();
  const payout = usePayout();
  const payoutStep = usePayoutSetupStep();
  const pixDestinationOptions = getPixDestinationOptions();
  const previousValues = payout[PayoutType.Fiat][BridgeFiatRail.PIX]?.form?.values;
  const [createBankAccount, { isLoading, error }] = useCreateMerchantsBankAccountMutation();
  const [updatePayoutSettings, { isLoading: isUpdatingPayoutSettings }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const form = useFormik<PixFormValues>({
    initialValues: previousValues ?? getPixInitialValues(),
    validationSchema: getPixValidationSchema(),
    onSubmit: async (values) => {
      const documentNumber = normalizeDocumentDigits(values.documentNumber);
      const destination = {
        [values.destinationType]: {
          [values.destinationType]:
            values.destinationType === PixDestinationType.PixKey ? values.destinationValue.toLowerCase() : values.destinationValue,
          ...(documentNumber ? { documentNumber } : {}),
        },
      };
      const response = await createBankAccount({
        uuid: payout.merchant.uuid,
        paymentRail: BridgeFiatRail.PIX,
        accountType: ExternalAccountType.PIX,
        currency: 'brl',
        bankName: values.bankName,
        accountOwnerName: values.accountOwnerName,
        ...destination,
      });

      if ('error' in response || !response.data?.data?.uuid) {
        return;
      }

      const shouldUpdatePaymentRail = payout.merchant.payoutFlow !== MerchantPayoutFlow.DIRECT;
      const payload: FiatPayoutSettingsPayload = {
        merchantId: payout.merchant.uuid,
        payoutDestinationUuid: response.data.data.uuid,
        payoutMemo: '',
        ...(shouldUpdatePaymentRail && {
          paymentRail: BridgeFiatRail.PIX,
        }),
      };
      const payoutSettingsResponse = await updatePayoutSettings(payload);
      if ('error' in payoutSettingsResponse) {
        Logger.error('Unable to update main account after PIX destination was created:', JSON.stringify(payoutSettingsResponse));
        return;
      }

      payoutStep.proceed();
    },
  });

  useEffect(() => {
    payout.setPayoutState({
      ...payout,
      [PayoutType.Fiat]: {
        ...payout[PayoutType.Fiat],
        [BridgeFiatRail.PIX]: { form },
      },
    });
  }, [form.values]);

  const destinationOption = pixDestinationOptions.find((option) => option.key === form.values.destinationType);
  const destinationValueTitle =
    form.values.destinationType === PixDestinationType.PixKey
      ? t(LABELS.CREATE_MODAL.SECTIONS.PIX_DESTINATION_VALUE_SECTION.PIX_KEY_TITLE)
      : t(LABELS.CREATE_MODAL.SECTIONS.PIX_DESTINATION_VALUE_SECTION.BR_CODE_TITLE);
  const destinationValuePlaceholder =
    form.values.destinationType === PixDestinationType.PixKey
      ? t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.PIX_KEY)
      : t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BR_CODE);
  const fieldError = (field: keyof PixFormValues) => (form.touched[field] ? (form.errors[field] as string | undefined) : undefined);

  return (
    <>
      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.PIX_DESTINATION_TYPE_SECTION.TITLE)}</Label>
        <FormField error={fieldError('destinationType')}>
          <Select<SelectItem, false>
            fullWidth
            closeDropdownOnSelect
            multiple={false}
            items={pixDestinationOptions}
            value={destinationOption}
            getItemLabel={(item) => item.label}
            onChange={(item) => form.setFieldValue('destinationType', (item as SelectItem).key as PixDestinationType)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{destinationValueTitle}</Label>
        <FormField error={fieldError('destinationValue')}>
          <Input
            value={form.values.destinationValue}
            onChange={(value) => form.setFieldValue('destinationValue', value)}
            placeholder={destinationValuePlaceholder}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.PIX_DOCUMENT_NUMBER_SECTION.TITLE)}</Label>
        <FormField error={fieldError('documentNumber')}>
          <Input
            value={form.values.documentNumber}
            onChange={(value) => form.setFieldValue('documentNumber', value)}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.PIX_DOCUMENT_NUMBER)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_OWNER_NAME_SECTION.TITLE)}</Label>
        <FormField error={fieldError('accountOwnerName')}>
          <Input
            value={form.values.accountOwnerName}
            onChange={(value) => form.setFieldValue('accountOwnerName', value)}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_OWNER_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BANK_NAME_SECTION.TITLE)}</Label>
        <FormField error={fieldError('bankName')}>
          <Input
            value={form.values.bankName}
            onChange={(value) => form.setFieldValue('bankName', value)}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BANK_NAME)}
          />
        </FormField>
      </Section>

      {Boolean((error as ReduxToolkitError)?.data?.errorMessage) && (
        <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage}</ErrorText>
      )}

      <Delimiter />
      <Buttons>
        <Button secondary onClick={payoutStep.goBack}>
          {t(LABELS.ACTIONS.BACK)}
        </Button>
        <ButtonWithLoading
          primary
          isLoading={isLoading || isUpdatingPayoutSettings}
          disabled={!form.isValid}
          onClick={() => form.handleSubmit()}
        >
          {t(LABELS.ACTIONS.ADD)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default PixBankAccountForm;
